import { isRecordLike } from '../../../../helpers';
import type {
    ResourcesSource,
    LocaleTranslations,
    TranslationKey,
    InterpolationParams,
    RegExpCacheMap
} from '../types';

/**
 * Gets or creates cached regex for placeholder key
 */
export const getOrCreatePlaceholderRegex = (
    cache: RegExpCacheMap,
    key: string
): RegExp => {
    let placeholderRegExp = cache.get(key);
    
    if (!placeholderRegExp) {
        placeholderRegExp = new RegExp(`{\\s*${key}\\s*}`, 'g');
        cache.set(key, placeholderRegExp);
    }

    return placeholderRegExp;
};

/**
 * Interpolates parameters in text string
 */
export const interpolateParameters = (
    text: string,
    params: InterpolationParams,
    regexCache: RegExpCacheMap
): string => {
    let result = text;

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined) {
            continue;
        }

        const placeholderRegExp = getOrCreatePlaceholderRegex(regexCache, key);

        result = result.replace(placeholderRegExp, String(value));
    }

    return result;
};

/**
 * Parses resources source into flat translations map
 */
export const parseResourcesSourceToTranslations = (
    resourcesSource: ResourcesSource
): LocaleTranslations => {
    const flatMap = new Map<TranslationKey, string>();

    const flattenObject = (
        obj: Record<string, unknown>,
        prefix: string = ''
    ): void => {
        for (const [key, value] of Object.entries(obj)) {
            const currentKey = prefix ? `${prefix}.${key}` : key;
            
            if (isRecordLike(value)) {
                flattenObject(value, currentKey);
                continue;
            }

            // TypeScript cannot infer that template literal matches TranslationKey pattern
            // Safe because: prefix is always provided (starts with resourceName), ensuring dot notation
            flatMap.set(currentKey , String(value));
        }
    };

    // Парсим каждый ресурс из источника
    for (const [resourceName, resource] of Object.entries(resourcesSource)) {
        flattenObject(resource, resourceName);
    }

    return flatMap;
};
